<script setup lang="ts">
import { ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { Lock, Pencil, Plus, Trash2 } from '@lucide/vue';
import { PRIVILEGI_PER_CATEGORIA, type Privilegio, type Ruolo } from '@evolfin/shared';
import { EvolfinApiError } from '@/api/client';
import { useAvvisi } from '@/composables/useAvvisi';
import { useInvalidation } from '@/composables/useInvalidation';
import { useConferma } from '@/composables/useConferma';
import PulsanteAzione from '@/components/ui/PulsanteAzione.vue';
import StatoCaricamento from '@/components/ui/StatoCaricamento.vue';
import FormRuoloDialog from '../components/FormRuoloDialog.vue';
import NavUtenti from '../components/NavUtenti.vue';
import { eliminaRuolo, listaRuoli } from '../api';

// Pagina di sola gestione: senza `can_manage_ruoli` il router la nasconde.
definePage({ meta: { titolo: 'Ruoli', privilegi: ['can_manage_ruoli'] } });

const avvisi = useAvvisi();
// Il ruolo compare anche nell'elenco utenti (colonna e filtro): invalidare solo
// questa pagina lo lascerebbe là col nome vecchio.
const invalidate = useInvalidation('ruoli', 'utenti');
const conferma = useConferma();

const { data: ruoli, isLoading } = useQuery({ queryKey: ['ruoli'], queryFn: listaRuoli });

const dialogAperto = ref(false);
const ruoloInModifica = ref<Ruolo | null>(null);

function apriCreazione(): void {
  ruoloInModifica.value = null;
  dialogAperto.value = true;
}
function apriModifica(r: Ruolo): void {
  ruoloInModifica.value = r;
  dialogAperto.value = true;
}

/**
 * Riepilogo dei privilegi PER CATEGORIA (etichetta + quanti su totale): scala
 * con la lista che cresce, dove elencare ogni privilegio come pill non reggerebbe.
 */
function riepilogoCategorie(
  privilegi: Privilegio[],
): { etichetta: string; n: number; tot: number }[] {
  const set = new Set(privilegi);
  return PRIVILEGI_PER_CATEGORIA.map((c) => ({
    etichetta: c.etichetta,
    n: c.privilegi.filter((p) => set.has(p)).length,
    tot: c.privilegi.length,
  })).filter((c) => c.n > 0);
}

async function elimina(r: Ruolo): Promise<void> {
  if (
    !(await conferma({
      titolo: 'Elimina ruolo',
      messaggio: `Eliminare il ruolo “${r.nome}”? L’operazione è possibile solo se non è assegnato ad alcun utente.`,
      etichettaConferma: 'Elimina',
      distruttiva: true,
    }))
  ) {
    return;
  }
  try {
    await eliminaRuolo(r.id);
    avvisi.successo('Ruolo eliminato');
    await invalidate();
  } catch (errore) {
    const messaggio =
      errore instanceof EvolfinApiError ? errore.message : 'Eliminazione non riuscita';
    avvisi.errore('Errore', messaggio);
  }
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <header class="flex items-end justify-between gap-4">
      <div>
        <p class="text-xs font-semibold tracking-[0.08em] text-ink-muted uppercase">Impostazioni</p>
        <h1 class="mt-1 font-display text-[33px] leading-[1.07] font-semibold">Ruoli</h1>
        <p class="mt-1 text-[13px] text-ink-secondary">
          Insiemi di privilegi assegnabili agli utenti.
        </p>
      </div>
      <PulsanteAzione @click="apriCreazione">
        Nuovo ruolo
        <template #icona><Plus :size="16" :stroke-width="2" /></template>
      </PulsanteAzione>
    </header>

    <NavUtenti />

    <StatoCaricamento v-if="isLoading" />

    <div v-else class="grid gap-4 sm:grid-cols-2">
      <article v-for="r in ruoli ?? []" :key="r.id" class="card flex flex-col gap-3 p-5">
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0">
            <h2 class="flex items-center gap-1.5 font-display text-[16px] font-semibold">
              {{ r.nome }}
              <Lock
                v-if="!r.editabile"
                :size="13"
                :stroke-width="2"
                class="text-ink-muted"
                title="Ruolo di sistema, non modificabile"
              />
            </h2>
            <p v-if="r.descrizione" class="mt-0.5 text-[12.5px] text-ink-secondary">
              {{ r.descrizione }}
            </p>
          </div>
          <div v-if="r.editabile" class="flex shrink-0 items-center gap-1">
            <button
              type="button"
              class="rounded-sm p-1.5 text-ink-muted transition-colors hover:bg-soft hover:text-ink"
              title="Modifica"
              @click="apriModifica(r)"
            >
              <Pencil :size="16" :stroke-width="1.8" />
            </button>
            <button
              type="button"
              class="rounded-sm p-1.5 text-ink-muted transition-colors hover:bg-soft hover:text-[var(--color-danger-text)]"
              title="Elimina"
              @click="elimina(r)"
            >
              <Trash2 :size="16" :stroke-width="1.8" />
            </button>
          </div>
        </div>

        <div v-if="r.privilegi.length" class="flex flex-col gap-1.5 border-t border-line-soft pt-3">
          <div
            v-for="cat in riepilogoCategorie(r.privilegi)"
            :key="cat.etichetta"
            class="flex items-center justify-between text-[12.5px]"
          >
            <span class="text-ink-secondary">{{ cat.etichetta }}</span>
            <span class="font-medium text-ink-muted tabular-nums">{{ cat.n }}/{{ cat.tot }}</span>
          </div>
        </div>
        <p v-else class="border-t border-line-soft pt-3 text-[12px] text-ink-muted">
          Nessun privilegio elevato (accesso per team).
        </p>
      </article>
    </div>

    <FormRuoloDialog v-model="dialogAperto" :ruolo="ruoloInModifica" @salvato="invalidate()" />
  </div>
</template>
